import {
    addDoc,
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getCountFromServer,
    getDocs,
    getFirestore,
    limit,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    startAfter,
    where,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';
import { createNoticeNotification } from './notificationsService';
import { isDirectoryTeacher } from './teacherService';

function db() {
    return getFirestore(getFirebaseApp());
}

function normalizeNoticeType(type) {
    return type === 'notice' ? 'notice' : 'announcement';
}

function getNoticesRootRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return collection(db(), COLLECTIONS.NOTICES_ANNOUNCEMENTS, tenantId, normalizeNoticeType('announcement'));
}

function getNoticesSummaryDocRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return doc(db(), COLLECTIONS.NOTICES_ANNOUNCEMENTS, tenantId);
}

function getNoticeCollectionRef(tenantId, type) {
    if (!tenantId) throw new Error('Missing tenantId');
    return collection(db(), COLLECTIONS.NOTICES_ANNOUNCEMENTS, tenantId, normalizeNoticeType(type));
}

function getNoticeDocRef(tenantId, noticeType, noticeId) {
    if (!noticeId) throw new Error('Missing noticeId');
    return doc(db(), COLLECTIONS.NOTICES_ANNOUNCEMENTS, tenantId, normalizeNoticeType(noticeType), noticeId);
}

function mapDoc(snap, noticeType) {
    return { id: snap.id, noticeType: normalizeNoticeType(noticeType), ...snap.data() };
}

function toMillis(value) {
    if (!value) return 0;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function calculateNoticeStatus(row) {
    const publishAtMs = toMillis(row?.publishAt);
    if (row?.isNotification) return 'sent';
    if (publishAtMs > Date.now()) return 'scheduled';
    return 'pending';
}

function buildNoticeSummary(tenantId, rows) {
    const summary = {
        tenantId,
        totalRecords: 0,
        totalNotices: 0,
        totalAnnouncements: 0,
        totalSent: 0,
        totalScheduled: 0,
        totalPending: 0,
        sentNotices: 0,
        sentAnnouncements: 0,
        scheduledNotices: 0,
        scheduledAnnouncements: 0,
        pendingNotices: 0,
        pendingAnnouncements: 0,
        byType: { notice: 0, announcement: 0 },
        byStatus: { pending: 0, scheduled: 0, sent: 0 },
    };
    (rows || []).forEach((row) => {
        const type = normalizeNoticeType(row?.noticeType || row?.type);
        const status = calculateNoticeStatus(row);
        summary.totalRecords += 1;
        summary.byType[type] += 1;
        summary.byStatus[status] += 1;
        if (type === 'notice') summary.totalNotices += 1;
        if (type === 'announcement') summary.totalAnnouncements += 1;
        if (status === 'sent') {
            summary.totalSent += 1;
            if (type === 'notice') summary.sentNotices += 1;
            else summary.sentAnnouncements += 1;
        }
        if (status === 'scheduled') {
            summary.totalScheduled += 1;
            if (type === 'notice') summary.scheduledNotices += 1;
            else summary.scheduledAnnouncements += 1;
        }
        if (status === 'pending') {
            summary.totalPending += 1;
            if (type === 'notice') summary.pendingNotices += 1;
            else summary.pendingAnnouncements += 1;
        }
    });
    return summary;
}

async function fetchAllNoticesRows(tenantId) {
    const [noticeSnap, announcementSnap] = await Promise.all([
        getDocs(query(getNoticeCollectionRef(tenantId, 'notice'), orderBy('createdAt', 'desc'))),
        getDocs(query(getNoticeCollectionRef(tenantId, 'announcement'), orderBy('createdAt', 'desc'))),
    ]);
    return [
        ...noticeSnap.docs.map((d) => mapDoc(d, 'notice')),
        ...announcementSnap.docs.map((d) => mapDoc(d, 'announcement')),
    ].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt));
}

async function refreshNoticesSummary(tenantId, rowsOverride = null) {
    const rows = Array.isArray(rowsOverride) ? rowsOverride : await fetchAllNoticesRows(tenantId);
    const summary = buildNoticeSummary(tenantId, rows);
    await setDoc(
        getNoticesSummaryDocRef(tenantId),
        {
            ...summary,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
    return summary;
}

function buildNotificationHistoryEntry({ actor, source, noticeType }) {
    return {
        sentAt: new Date(),
        source: source || 'manual',
        type: noticeType === 'notice' ? 'notice' : 'announcement',
        actor: {
            uid: actor?.uid || null,
            email: actor?.email || null,
            name: actor?.name || null,
        },
    };
}

export async function fetchNoticesPage({ tenantId, pageSize = 10, cursor = null }) {
    const allRows = await fetchAllNoticesRows(tenantId);

    // Cursor fallback: numeric offset by page, while keeping existing hook contract.
    const offset = cursor?.offset || 0;
    const pageRows = allRows.slice(offset, offset + pageSize);
    const nextOffset = offset + pageRows.length;
    const nextCursor = nextOffset < allRows.length ? { offset: nextOffset } : null;

    const summarySnap = await getDoc(getNoticesSummaryDocRef(tenantId));
    const summaryData = summarySnap.exists() ? summarySnap.data() : null;
    const computedSummary = buildNoticeSummary(tenantId, allRows);
    const analytics = {
        total: summaryData?.totalRecords ?? computedSummary.totalRecords,
        byType: summaryData?.byType || computedSummary.byType,
        byStatus: summaryData?.byStatus || computedSummary.byStatus,
    };

    return { rows: pageRows, nextCursor, total: allRows.length, analytics };
}

export async function createNoticeAnnouncement({ tenantId, payload, createdBy }) {
    const noticeType = normalizeNoticeType(payload?.type);
    const baseRef = getNoticeCollectionRef(tenantId, noticeType);
    const prepared = {
        ...payload,
        type: noticeType,
        tenantId,
        createdBy: createdBy || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };
    const created = await addDoc(baseRef, prepared);
    if (payload?.isNotification === true) {
        try {
            await createNoticeNotification({
                tenantId,
                noticeId: created.id,
                noticePayload: payload,
                createdBy,
            });
            await setDoc(
                getNoticeDocRef(tenantId, noticeType, created.id),
                {
                    isNotification: true,
                    notifiedAt: serverTimestamp(),
                    notificationHistory: arrayUnion(buildNotificationHistoryEntry({
                        actor: createdBy,
                        source: 'create_immediate',
                        noticeType: payload?.type,
                    })),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } catch (error) {
            console.warn('Failed to create/send notice notification', error);
        }
    }
    await refreshNoticesSummary(tenantId);
    return { id: created.id, noticeType };
}

async function resolveNoticeDocRef({ tenantId, noticeId, noticeType }) {
    if (noticeType) return { ref: getNoticeDocRef(tenantId, noticeType, noticeId), noticeType: normalizeNoticeType(noticeType) };
    const noticeRef = getNoticeDocRef(tenantId, 'notice', noticeId);
    const noticeSnap = await getDoc(noticeRef);
    if (noticeSnap.exists()) return { ref: noticeRef, noticeType: 'notice' };
    const announcementRef = getNoticeDocRef(tenantId, 'announcement', noticeId);
    const announcementSnap = await getDoc(announcementRef);
    if (announcementSnap.exists()) return { ref: announcementRef, noticeType: 'announcement' };
    throw new Error('Notice not found');
}

export async function updateNoticeAnnouncement({ tenantId, noticeId, noticeType, patch }) {
    if (!noticeId) throw new Error('Missing noticeId');
    const { ref: noticeRef } = await resolveNoticeDocRef({ tenantId, noticeId, noticeType });
    await setDoc(
        noticeRef,
        {
            ...patch,
            type: normalizeNoticeType(patch?.type || noticeType),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
    await refreshNoticesSummary(tenantId);
    return { noticeId, noticeType: normalizeNoticeType(noticeType) };
}

export async function deleteNoticeAnnouncement({ tenantId, noticeId, noticeType }) {
    if (!noticeId) throw new Error('Missing noticeId');
    const { ref: noticeRef } = await resolveNoticeDocRef({ tenantId, noticeId, noticeType });
    await deleteDoc(noticeRef);
    await refreshNoticesSummary(tenantId);
    return { noticeId };
}

export async function sendNoticeNotificationNow({
    tenantId,
    noticeId,
    noticeType,
    actor,
    force = false,
    source = 'manual',
    markAsSent = true,
}) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!noticeId) throw new Error('Missing noticeId');
    const { ref: noticeRef } = await resolveNoticeDocRef({ tenantId, noticeId, noticeType });
    const snap = await getDoc(noticeRef);
    if (!snap.exists()) throw new Error('Notice not found');
    const notice = { id: snap.id, ...snap.data() };
    if (notice?.isNotification && !force) {
        return { noticeId, sent: false, alreadySent: true };
    }
    await createNoticeNotification({
        tenantId,
        noticeId,
        noticePayload: notice,
        createdBy: actor || notice?.createdBy || null,
    });
    await setDoc(
        noticeRef,
        markAsSent
            ? {
                isNotification: true,
                notifiedAt: serverTimestamp(),
                notificationHistory: arrayUnion(buildNotificationHistoryEntry({
                    actor: actor || notice?.createdBy || null,
                    source,
                    noticeType: notice?.type,
                })),
                updatedAt: serverTimestamp(),
            }
            : {
                notificationHistory: arrayUnion(buildNotificationHistoryEntry({
                    actor: actor || notice?.createdBy || null,
                    source,
                    noticeType: notice?.type,
                })),
                updatedAt: serverTimestamp(),
            },
        { merge: true }
    );
    await refreshNoticesSummary(tenantId);
    return { noticeId, sent: true };
}

export async function dispatchDueNoticeNotifications({ tenantId }) {
    if (!tenantId) return { sentCount: 0 };
    const now = new Date();
    const baseRef = getNoticeCollectionRef(tenantId, 'announcement');
    const q = query(
        baseRef,
        where('isNotification', '==', false),
        where('publishAt', '<=', now),
        limit(20)
    );
    const snap = await getDocs(q);
    if (!snap.docs.length) return { sentCount: 0 };
    let sentCount = 0;
    for (const row of snap.docs) {
        try {
            await sendNoticeNotificationNow({
                tenantId,
                noticeId: row.id,
                noticeType: 'announcement',
                actor: row.data()?.createdBy || null,
                source: 'scheduler_due',
            });
            sentCount += 1;
        } catch (error) {
            console.warn('Failed due-notice dispatch', row.id, error);
        }
    }
    return { sentCount };
}

function normalizeAudienceRows(rows, role) {
    return (rows || []).map((row) => {
        const id = row.id || row.authUid || row.uid;
        const label = row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || id;
        return {
            id,
            authUid: row.authUid || row.uid || row.id || null,
            role,
            label,
            email: row.email || null,
            fcmToken: row.fcmToken || null,
        };
    }).filter((row) => !!row.id);
}

export async function fetchNoticeAudienceUsers({ tenantId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const dbInstance = db();
    const [studentsSnap, teachersSnap, staffSnap] = await Promise.all([
        getDocs(collection(dbInstance, 'Students', tenantId, 'students')),
        getDocs(collection(dbInstance, 'Teachers', tenantId, 'teachers')),
        getDocs(collection(dbInstance, 'staff', tenantId, 'staff')),
    ]);

    const students = normalizeAudienceRows(studentsSnap.docs.map(mapDoc), 'student');
    const teachers = normalizeAudienceRows(
        teachersSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isDirectoryTeacher),
        'teacher'
    );
    const staff = normalizeAudienceRows(staffSnap.docs.map(mapDoc), 'staff');

    return [...students, ...teachers, ...staff];
}
